import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { KcIconComponent, ResponsiveService } from '@kaspacom/ui-kit';
import { WalletChipComponent } from '../wallet-chip/wallet-chip.component';
import { ThemeToggleComponent } from '../theme-toggle/theme-toggle.component';
import { NotificationsBellComponent } from '../notifications-bell/notifications-bell.component';
import {
  NavBreadcrumbComponent,
  NavCrumb,
} from '../nav-breadcrumb/nav-breadcrumb.component';
import { environment } from '../../../environments/environment';
import { WalletManager } from '../../managers/wallet.manager';
import { formatSompi } from '../../shared/kcc20-formatters';
import { kasToSompi } from '../../utils/number-format.util';

const ROUTE_CRUMBS: { test: RegExp; crumbs: string[] }[] = [
  {
    test: /^\/migration-admin\/[^/]+/,
    crumbs: ['KaspaCom', 'Migration admin', 'Workflow'],
  },
  {
    test: /^\/migration-admin/,
    crumbs: ['KaspaCom', 'Migration admin'],
  },
  {
    test: /^\/migrations\/[^/]+\/claim/,
    crumbs: ['KaspaCom', 'Migrations', 'Claim'],
  },
  {
    test: /^\/migrations\/[^/]+/,
    crumbs: ['KaspaCom', 'Migrations', 'Details'],
  },
  { test: /^\/migrations/, crumbs: ['KaspaCom', 'Migrations'] },
  { test: /^\/tokens\/[^/]+\/manage/, crumbs: ['Markets', 'Token', 'Manage'] },
  { test: /^\/tokens\/[^/]+/, crumbs: ['Markets', 'Token'] },
  { test: /^\/markets/, crumbs: ['KaspaCom', 'Markets'] },
  { test: /^\/mints/, crumbs: ['KaspaCom', 'Mints'] },
  { test: /^\/portfolio/, crumbs: ['KaspaCom', 'Portfolio'] },
  { test: /^\/rewards/, crumbs: ['KaspaCom', 'Rewards'] },
  { test: /^\/deploy-token/, crumbs: ['KaspaCom', 'Deploy token'] },
  { test: /^\/import-token/, crumbs: ['KaspaCom', 'Import token'] },
  { test: /^\/admin/, crumbs: ['KaspaCom', 'Admin'] },
  { test: /^\/faq/, crumbs: ['KaspaCom', 'FAQ'] },
];

function buildCrumbs(url: string): NavCrumb[] {
  const path = url.split('?')[0].split('#')[0];
  const match = ROUTE_CRUMBS.find((r) => r.test.test(path));
  const labels = match?.crumbs ?? ['KaspaCom'];
  return labels.map((label, i) => ({
    label,
    isCurrent: i === labels.length - 1,
  }));
}

function networkDisplayName(network: string): string {
  if (network === 'testnet-10') {
    return 'Testnet-10';
  }
  if (network === 'mainnet') {
    return 'Mainnet';
  }
  return network
    .split('-')
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join('-');
}

@Component({
  selector: 'kcc20-defi-nav-bar',
  standalone: true,
  imports: [
    WalletChipComponent,
    ThemeToggleComponent,
    NavBreadcrumbComponent,
    NotificationsBellComponent,
    KcIconComponent,
  ],
  templateUrl: './defi-nav-bar.component.html',
  styleUrl: './defi-nav-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DefiNavBarComponent {
  private readonly responsiveService = inject(ResponsiveService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly walletManager = inject(WalletManager);

  protected readonly networkName = networkDisplayName(environment.kaspaNetwork);
  protected readonly currentUrl = signal(this.router.url);

  protected readonly isMobile = computed(
    () => this.responsiveService.deviceType() !== 'desktop',
  );

  protected readonly crumbs = computed(() => buildCrumbs(this.currentUrl()));

  protected readonly pageTitle = computed(() => {
    const c = this.crumbs();
    return c[c.length - 1]?.label ?? 'KCC20';
  });

  /** Same KAS grouping / suffix as the markets price column, capped at 2dp. */
  protected readonly balanceLabel = computed(() =>
    formatSompi(
      kasToSompi(this.walletManager.walletKaspaBalance().toFixed(8)),
      2,
    ),
  );

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => this.currentUrl.set(e.urlAfterRedirects));
  }
}
